import { useMutation, useQueryClient } from "react-query";
import { makeAuthedRequest } from "utils/makeAuthedRequest";

const useConfirmBankInfo = () => {
  const queryClient = useQueryClient();

  const confirmBankInfo = async ({ entityId, bankInfo }) => {
    const response = await makeAuthedRequest(
      `v1/settings/sub-entity/ach/confirm/${entityId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ bankInfo }),
      }
    );

    if (!response.ok) {
      const body = await response.json();
      throw new Error(body.message || "Failed to confirm bank information");
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {};
  };

  return useMutation(confirmBankInfo, {
    onSuccess: () => {
      queryClient.invalidateQueries(["subEntityInfo"]);
    },
  });
};

const useUpdateBankInfo = () => {
  const queryClient = useQueryClient();

  const updateBankInfo = async ({ entityId, updatedAch }) => {
    const response = await makeAuthedRequest(
      `v1/settings/sub-entity/ach/${entityId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updatedAch }),
      }
    );

    if (!response.ok) {
      const body = await response.json();
      throw new Error(body.message || "Failed to update bank information");
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {};
  };

  return useMutation(updateBankInfo, {
    onSuccess: () => {
      queryClient.invalidateQueries(["subEntityInfo"]);
    },
  });
};

const useUploadBankDoc = () => {
  const queryClient = useQueryClient();

  const uploadBankDoc = async ({ entityId, fileData }) => {
    const response = await makeAuthedRequest(
      `v1/settings/sub-entity/ach/upload/${entityId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ fileData }),
      }
    );

    if (!response.ok) {
      const body = await response.json();
      throw new Error(body.message || "Failed to upload document");
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {};
  };

  return useMutation(uploadBankDoc, {
    onSuccess: () => {
      queryClient.invalidateQueries(["subEntityInfo"]);
    },
  });
};

const useCancelAchRequest = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ entityId }) => {
      const response = await makeAuthedRequest(
        `v1/settings/sub-entity/ach/cancel/${entityId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to cancel request");
      }

      const text = await response.text();
      return text ? JSON.parse(text) : {};
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["subEntityInfo"]);
      },
    }
  );
};

export {
  useConfirmBankInfo,
  useUpdateBankInfo,
  useUploadBankDoc,
  useCancelAchRequest,
};
